// import { Duration as ProblematicDuration } from '../interfaces'; // Rename old type - Removed as unused

// Define a clear type for the structured duration object used internally
export type DurationObject = {
    value: number;
    unit: 'MINUTES' | 'HOURS' | 'DAYS' | 'WEEKS' | 'MONTHS';
};

// Mapping from short unit codes to backend/object unit codes
const shortToUnitMap: { [key: string]: DurationObject['unit'] } = {
    mi: 'MINUTES',
    h: 'HOURS',
    d: 'DAYS',
    w: 'WEEKS',
    mo: 'MONTHS',
};

// Added: Mapping from backend/object unit codes back to short unit codes
const unitToShortMap: { [key in DurationObject['unit']]: string } = {
    MINUTES: 'mi',
    HOURS: 'h',
    DAYS: 'd',
    WEEKS: 'w',
    MONTHS: 'mo',
};

// Mapping from backend/object unit codes to readable names (singular/plural)
const unitToReadableMap: {
    [key in DurationObject['unit']]: { singular: string; plural: string };
} = {
    MINUTES: { singular: 'Min', plural: 'Mins' }, // Adjusted for consistency
    HOURS: { singular: 'Hour', plural: 'Hours' },
    DAYS: { singular: 'Day', plural: 'Days' },
    WEEKS: { singular: 'Week', plural: 'Weeks' },
    MONTHS: { singular: 'Mon', plural: 'Mons' }, // Adjusted for consistency
};

// Removed the complex readableToShortMap
// const readableToShortMap: { [key: string]: string } = { ... };

/**
 * Converts a readable duration string (e.g., "1 Day", "30 Mins") produced by the system
 * back to its short string representation (e.g., "1d", "30mi") for editing.
 * Returns an empty string if the input is null, undefined, or cannot be parsed.
 */
export const readableDurationStringToShortString = (
    readableString: string | null | undefined
): string => {
    if (!readableString) {
        return '';
    }
    const parts = readableString.trim().split(/\s+/);
    if (parts.length !== 2) {
        return ''; // Expecting "value unitName" format
    }
    const valueStr = parts[0];
    const unitNameStr = parts[1]; // Case sensitive based on unitToReadableMap

    const value = parseFloat(valueStr);
    if (isNaN(value)) {
        return ''; // Invalid number
    }

    let foundUnitKey: DurationObject['unit'] | null = null;
    for (const key in unitToReadableMap) {
        const unitKey = key as DurationObject['unit'];
        const readableNames = unitToReadableMap[unitKey];
        if (
            unitNameStr === readableNames.singular ||
            unitNameStr === readableNames.plural
        ) {
            foundUnitKey = unitKey;
            break;
        }
    }

    if (!foundUnitKey) {
        return ''; // Unit name doesn't match known readable names
    }

    const shortUnit = unitToShortMap[foundUnitKey];
    if (!shortUnit) {
        return ''; // Should not happen if maps are consistent
    }

    // Format consistently - Use String() to avoid trailing zeros
    const formattedValue = String(value);

    return `${formattedValue}${shortUnit}`;
};

/**
 * Converts a short duration string (e.g., "1d", "30mi") to a Duration object.
 * Returns null if the format is invalid.
 */
export const shortStringToDurationObject = (
    shortString: string | null | undefined
): DurationObject | null => {
    if (!shortString) {
        return null;
    }
    const match = shortString.match(/^(\d+(?:\.\d{0,2})?)(mi|h|d|w|mo)$/);
    if (!match) {
        return null; // Invalid format
    }
    const value = parseFloat(match[1]);
    const shortUnit = match[2];
    const unit = shortToUnitMap[shortUnit];

    if (isNaN(value) || !unit) {
        return null; // Invalid value or unit
    }

    return { value, unit }; // No cast needed now
};

/**
 * Converts a short duration string (e.g., "1d", "30mi") back to the standard human-readable string
 * expected by the backend (e.g., "1 Day", "30 Mins").
 * Returns an empty string if the format is invalid or input is empty.
 */
export const formatShortDurationForDisplay = (
    shortString: string | null | undefined
): string => {
    if (!shortString) {
        return '';
    }
    const durationObj = shortStringToDurationObject(shortString);
    if (!durationObj) {
        return '';
    }

    // Use the unit key directly from the object
    const readableUnits = unitToReadableMap[durationObj.unit];
    if (!readableUnits) {
        return ''; // Should not happen
    }

    // Format consistently - Use String() to avoid trailing zeros
    const formattedValue = String(durationObj.value);

    return `${formattedValue} ${
        // Use formatted value
        // Handle pluralization correctly even for non-integer values like 1.0
        durationObj.value === 1 ? readableUnits.singular : readableUnits.plural
    }`;
};

/**
 * Converts a Duration object directly to a human-readable string.
 * Returns an empty string if the input is null or undefined.
 * (Likely unused if backend stores readable strings)
 */
/*
export const formatDurationObjectForDisplay = (
    duration: DurationObject | null | undefined
): string => {
    if (!duration?.value || !duration?.unit) {
        return '';
    }
    const readableUnits = unitToReadableMap[duration.unit];
    if (!readableUnits) {
        return '';
    }
     // Format consistently (e.g., no decimal for integer values)
    const formattedValue = Number.isInteger(duration.value)
        ? String(duration.value)
        : duration.value.toFixed(2);

    return `${formattedValue} ${ // Use formatted value
        duration.value === 1 ? readableUnits.singular : readableUnits.plural
    }`;
};
*/
